import {Box, Button, Container, Flex, Text} from '@chakra-ui/react';
import {useQuery} from '@tanstack/react-query';
import {geoSearchByStreet, geoSearchByText, getInDeliveryArea} from 'api/requests';
import {colors, useAddressContext} from 'components/providers';
import {InputSearchWithDebounce} from 'components/shared';
import {useToast, useTranslation} from 'hooks';
import {FC, useEffect, useMemo, useState} from 'react';
import {LocalMapMdList, MapMdList, MapMdProxyResponse} from 'types';
import {getStreetNameFromAddress, trimAddressSearchValue} from 'utils/addresses';
import logger from 'utils/logger';

import {LocationItem} from './components';

interface Props {
  onChange: (locationAddress?: any) => void;
  locationAddress?: any;
  setCoords: any;
  isLoading: boolean;
  isError: boolean;
  setSelectedBuildingFromSearch: any;
  showMap: () => void;
  mapShowed?: boolean;
}

export const LocationSearch: FC<Props> = ({
  locationAddress,
  onChange,
  setCoords,
  isLoading,
  isError,
  setSelectedBuildingFromSearch,
  showMap,
  mapShowed,
}) => {
  const toast = useToast();
  const {t} = useTranslation();
  const [searchValue, setSearchValue] = useState(locationAddress ? getStreetNameFromAddress(locationAddress) : '');
  const {setMapAddressChanged} = useAddressContext();

  const [suggestionsByKey, setSuggestionsByKey] = useState<Record<string, MapMdList[]>>({});
  const [showSuggestions, setShowSuggestions] = useState(false);

  useEffect(() => {
    if (locationAddress && searchValue !== getStreetNameFromAddress(locationAddress)) {
      setSearchValue(getStreetNameFromAddress(locationAddress));
      setShowSuggestions(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationAddress]);

  useEffect(() => {
    if (searchValue !== getStreetNameFromAddress(locationAddress)) setMapAddressChanged(true);
    else setMapAddressChanged(false);
  }, [locationAddress, searchValue, setMapAddressChanged]);

  const trimmedSearchValue = trimAddressSearchValue(searchValue);

  const {isFetching: isQuerySearchFetching, refetch} = useQuery({
    queryKey: ['locations', trimmedSearchValue],
    queryFn: () => geoSearchByText(trimmedSearchValue),
    enabled: searchValue?.length > 2,
    select: (data: MapMdProxyResponse) => {
      const list = data.list ?? [];
      return list.filter((item: MapMdList) => {
        return item?.parent_name;
      });
    },
    onSuccess(data) {
      setSuggestionsByKey((value) => ({
        ...value,
        [trimmedSearchValue]: data,
      }));
    },
  });

  useEffect(() => {
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClick = async (streetId: string, building: string) => {
    setShowSuggestions(false);
    const data: any = await geoSearchByStreet(streetId, building);
    logger.addBreadcrumb('geoSearchByStreet', data);
    if (data == ' ' || !data?.centroid) {
      return toast.error(t('LOCATION_SEARCH_ERROR'));
    }
    const isDeliverableLocation = await getInDeliveryArea(data.centroid, `&area_type=${'normal'}`);
    if (['outside_delivery_area', 'outside'].includes(isDeliverableLocation?.result)) {
      return toast.error(t('LOCATION_OUTSIDE_DELIVERY'));
    }

    setSelectedBuildingFromSearch(data);
    setCoords(data.centroid);
  };

  const handleClear = () => {
    onChange(undefined);
    setSearchValue('');
  };

  const handleInputChangeWithDebounce = (value: string) => {
    setSearchValue(value);
  };

  const filteredSuggestions = useMemo(() => {
    return suggestionsByKey[trimmedSearchValue] || [];
  }, [suggestionsByKey, trimmedSearchValue]);

  const filteredByBuilding = useMemo(() => {
    return filteredSuggestions.flatMap((suggestion) =>
      suggestion.buildings
        .filter((building) => {
          const buildingNumber = searchValue.match(/(\d+\/?\d*)/g)?.join('') || '';
          return building.toLowerCase().includes(buildingNumber);
        })
        .map((building) => ({...suggestion, building, buildings: []})),
    );
  }, [filteredSuggestions, searchValue]);

  return (
    <Box position={mapShowed ? 'absolute' : 'unset'} top="50px" w="90%" justifyContent="center">
      <Box onFocus={() => setShowSuggestions(true)} position="relative">
        <InputSearchWithDebounce
          isLoading={isLoading || isQuerySearchFetching}
          searchValue={searchValue}
          placeholder={t('SEARCH_ADDRESS_PLACEHOLDER')}
          onChangeValueWithDebounce={handleInputChangeWithDebounce}
          onClearSearchField={handleClear}
          cssPropsForInput={{
            borderColor: isError ? 'red' : '',
          }}
        />
        {showSuggestions && !isQuerySearchFetching && !isLoading && !!searchValue.length && (
          <Container
            variant="white-block"
            position="absolute"
            border="1px solid"
            borderColor={colors.lightGray}
            boxShadow="sm"
            top="46px"
            maxH="335px"
            overflowY="scroll"
            p="12px"
            mt="3px"
            zIndex="9"
          >
            {filteredByBuilding?.length ? (
              <Box w="full">
                {filteredByBuilding.map((location: LocalMapMdList) => {
                  const key = `${location.id + location.building}`;
                  return <LocationItem key={key} location={location} onClick={handleClick} />;
                })}
              </Box>
            ) : (
              <Flex justifyContent="space-between" alignItems="center">
                <Text>{t('showMapInfo')}</Text>
                <Button
                  onClick={() => {
                    handleClear();
                    showMap();
                  }}
                  variant="primary"
                >
                  {t('showMap')}
                </Button>
              </Flex>
            )}
          </Container>
        )}
        {isError && (
          <Text color="red" position="absolute" ml="5px">
            Selected location is outside Straus delivery zone
          </Text>
        )}
      </Box>
    </Box>
  );
};
